import type { Palette } from './image';

/** `pal.dat`: 256 × BGR → RGBA palette (alpha 255). */
export function decodePaletteDat(data: Uint8Array): Palette {
  const p = new Uint8Array(1024);
  for (let i = 0; i < 256; i++) {
    p[i * 4] = data[i * 3 + 2];
    p[i * 4 + 1] = data[i * 3 + 1];
    p[i * 4 + 2] = data[i * 3];
    p[i * 4 + 3] = 255;
  }
  return p;
}

/** A 256-entry palette index remap. */
export type PaletteTransform = Uint8Array;

/**
 * `.pl2` palette transforms: precomputed index→index lookup tables the original renderer used
 * for lighting, blending and text colors. We keep the parts the WebGL renderer needs as remaps.
 */
export interface Pl2 {
  base: Palette;
  lightLevels: PaletteTransform[]; // 32: darkest → brightest
  invColorVariations: PaletteTransform[]; // 16
  selectedUnitShift: PaletteTransform;
  hueVariations: PaletteTransform[]; // 111
  redTones: PaletteTransform;
  greenTones: PaletteTransform;
  blueTones: PaletteTransform;
  darkenedShift: PaletteTransform;
  textColors: [number, number, number][]; // 13 RGB colors
  textColorShifts: PaletteTransform[]; // 13
}

export const PL2_SIZE = 443175;

export function decodePl2(data: Uint8Array): Pl2 {
  if (data.length < PL2_SIZE) throw new Error(`pl2: unexpected size ${data.length}`);
  let o = 0;
  const take = (n: number) => {
    const v = data.subarray(o, o + n);
    o += n;
    return v;
  };
  const transforms = (n: number) => Array.from({ length: n }, () => take(256));
  const baseRaw = take(1024);
  const base = new Uint8Array(1024);
  for (let i = 0; i < 256; i++) {
    // Stored as R, G, B, unused.
    base[i * 4] = baseRaw[i * 4];
    base[i * 4 + 1] = baseRaw[i * 4 + 1];
    base[i * 4 + 2] = baseRaw[i * 4 + 2];
    base[i * 4 + 3] = 255;
  }
  const lightLevels = transforms(32);
  const invColorVariations = transforms(16);
  const selectedUnitShift = take(256);
  o += 3 * 256 * 256; // alpha blend tables
  o += 256 * 256; // additive
  o += 256 * 256; // multiplicative
  const hueVariations = transforms(111);
  const redTones = take(256);
  const greenTones = take(256);
  const blueTones = take(256);
  o += 14 * 256; // unknown variations
  o += 256 * 256; // max component blend
  const darkenedShift = take(256);
  const tc = take(39);
  const textColors: [number, number, number][] = [];
  for (let i = 0; i < 13; i++) textColors.push([tc[i * 3], tc[i * 3 + 1], tc[i * 3 + 2]]);
  const textColorShifts = transforms(13);
  return {
    base,
    lightLevels,
    invColorVariations,
    selectedUnitShift,
    hueVariations,
    redTones,
    greenTones,
    blueTones,
    darkenedShift,
    textColors,
    textColorShifts,
  };
}
